/**
 * Detects native support for scrollIntoView({ container: 'nearest' }).
 */
export function detectScrollIntoViewContainerSupport() {
    // SSR / non-browser environments: cannot detect, assume support
    if (typeof document === 'undefined' || !('createElement' in document)) {
        return Promise.resolve(true);
    }

    return new Promise<boolean>(resolve => {
        // Build an off-screen test DOM that must scroll the *inner* container only
        const outer = document.createElement('div');
        const inner = document.createElement('div');
        const spacerTop = document.createElement('div');
        const target = document.createElement('div');
        const spacerBottom = document.createElement('div');

        Object.assign(outer.style, {
            position: 'fixed',
            // put it off-screen to avoid flicker but keep in layout
            left: '-9999px',
            top: '0',
            width: '200px',
            height: '200px',
            overflow: 'auto',
            // isolate scrolling behavior
            contain: 'layout style paint',
            // avoid smooth scrolling delays
            scrollBehavior: 'auto',
        });

        Object.assign(inner.style, {
            width: '300px',
            height: '300px',
            overflow: 'auto',
        });

        // Make content tall so both containers can scroll independently.
        // IMPORTANT: The top spacer MUST be taller than the inner container's height so that
        // the target starts OUTSIDE of the inner viewport. Previously it was shorter which
        // meant the target was already visible and compliant browsers did not need to scroll,
        // causing us to wrongly conclude lack of support (e.g. in Chrome).
        spacerTop.style.height = '400px'; // > inner height (300px) so inner must scroll to reveal target
        spacerBottom.style.height = '250px';
        Object.assign(target.style, {height: '20px', background: 'transparent'});

        inner.append(spacerTop, target, spacerBottom);
        outer.append(inner);
        document.body.appendChild(outer);

        // Ensure both are at top initially
        outer.scrollTop = 0;
        inner.scrollTop = 0;

        // Call the API with the proposed option
        try {
            target.scrollIntoView({
                block: 'nearest',
                inline: 'nearest',
                behavior: 'instant',
                // the bit we want to detect:
                // @ts-expect-error not in standard lib yet
                container: 'nearest',
            });
        } catch {
            // Some engines might still throw (older builds) → definitely unsupported
            cleanup(false);
            return;
        }

        // Give the UA a tick to apply scrolling
        requestAnimationFrame(() => {
            // What we'd expect if supported:
            // - inner.scrollTop > 0 (scrolled to reveal target that started below viewport)
            // - outer.scrollTop === 0 (did *not* bubble to outer)
            const supported = inner.scrollTop > 0 && outer.scrollTop === 0;
            cleanup(supported);
        });

        function cleanup(result: boolean) {
            outer.remove();
            resolve(result);
        }
    });
}
